import { useState } from 'react';
import { apiPost } from 'api/typed';
import { push } from 'connected-react-router';
import firebase from 'firebase/app';
import { EResponseTypes } from 'lib/types';
import AuthActions from 'redux/auth';
import { useAppDispatch } from 'redux/hooks';
import { logAndCaptureException } from 'utils';
import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import classNames from 'classnames';
import ToastActions from 'redux/toast';
import { SigninProviderType } from 'lib/enums/SigninProviderType';
import { logInfo } from 'utils/logger';
import { ColumnService } from 'lib/services/directory';
import microsoftIcon from './loginIcons/microsoftIcon.svg';
import PromptForPasswordModal from './PromptForPasswordModal';
import { FirebaseAuthError } from './types';
import { errorMsg } from './utils';

export default function MicrosoftSignin() {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<FirebaseAuthError>();

  const handleLogin = async () => {
    try {
      const microsoftProvider = new firebase.auth.OAuthProvider(
        'microsoft.com'
      );
      microsoftProvider.setCustomParameters({
        prompt: 'consent' // Get Consent from user to access their basic info (optional - Reommended only during SignUp)
      });
      const result = await firebase.auth().signInWithPopup(microsoftProvider);
      const credentialData: firebase.auth.OAuthCredential | null =
        result.credential;
      const token = credentialData?.accessToken;
      const profile = result.additionalUserInfo?.profile as Record<
        string,
        string
      >;
      const uid = result.user?.uid;
      if (!(profile && token && uid)) return;
      setLoading(true);
      // Exchange the Microsoft Sign In token for a Firebase auth token
      const res: EResponseTypes['auth/signin-exchange-token-for-google-microsoft-provider'] =
        await apiPost(
          'auth/signin-exchange-token-for-google-microsoft-provider',
          {
            token,
            userProfile: profile,
            uid,
            type: SigninProviderType.MICROSOFT
          }
        );
      if (res.success) {
        dispatch(AuthActions.loginToken(res.firebaseToken));
        setLoading(false);
        // Go to / and let our routingSaga take user to the correct route
        dispatch(push('/'));
      } else {
        setLoading(false);
        dispatch(
          ToastActions.toastError({
            headerText: 'Error',
            bodyText: errorMsg
          })
        );
      }
    } catch (error) {
      const authError = error as FirebaseAuthError;
      const { code, email, credential } = authError;

      if (code === 'auth/account-exists-with-different-credential') {
        try {
          const existingProviders = await firebase
            .auth()
            .fetchSignInMethodsForEmail(email);

          if (existingProviders.length) {
            if (existingProviders[0] === 'google.com') {
              await firebase
                .auth()
                .signInWithPopup(new firebase.auth.GoogleAuthProvider());
              if (credential && firebase.auth().currentUser) {
                logInfo('User linking Microsoft account with Google', {
                  email
                });
                await firebase
                  .auth()
                  .currentUser?.linkWithCredential(credential);
                dispatch(push('/'));
              }
            } else if (existingProviders[0] === 'password') {
              setAuthError(authError);
            }
          }
        } catch (error) {
          dispatch(
            ToastActions.toastError({
              headerText: 'Something went wrong',
              bodyText: errorMsg
            })
          );
          logAndCaptureException(
            ColumnService.AUTH_AND_USER_MANAGEMENT,
            error,
            'Failed to fetch and link with new signin provider',
            { email }
          );
        }
      } else {
        dispatch(
          ToastActions.toastError({
            headerText: 'Error',
            bodyText: errorMsg
          })
        );
        logAndCaptureException(
          ColumnService.AUTH_AND_USER_MANAGEMENT,
          error,
          'Failed to signin with Microsoft'
        );
      }
    }
  };

  return (
    <>
      <button
        onClick={handleLogin}
        className={classNames(
          'w-full flex p-2.5 border border-column-gray-200 rounded-md bg-white focus:outline-none',
          {
            'hover:bg-column-gray-50 justify-center': !loading
          }
        )}
      >
        <img src={microsoftIcon} className="h-6 w-6" />
        <span className="font-medium text-column-gray-500 text-base pl-2">
          Microsoft
        </span>
        {loading && (
          <div className="flex w-full justify-end">
            <LoadingSpinner inline />
          </div>
        )}
      </button>
      {authError && (
        <PromptForPasswordModal
          onClose={() => setAuthError(undefined)}
          userAuthData={authError}
        />
      )}
    </>
  );
}
